import { reactive, computed } from 'vue'
import * as Data from '@/data/mockData.js'

export const MODULES = [
  { id: 'pharmacyMaster',    label: 'Pharmacy Master',     icon: 'store'     },
  { id: 'orderManagement',   label: 'Order Booking',       icon: 'clipboard' },
  { id: 'orderTracking',     label: 'Order Tracking',      icon: 'truck'     },
  { id: 'invoiceBilling',    label: 'Invoice & Billing',   icon: 'file'      },
  { id: 'stockDashboard',    label: 'Stock Dashboard',     icon: 'boxes'     },
  { id: 'oosDashboard',      label: 'Out of Stock',        icon: 'alert'     },
  { id: 'pharmacyProfile',   label: 'My Profile',          icon: 'store'     },
  { id: 'campaignView',      label: 'My Promotions',       icon: 'megaphone' },
  { id: 'pobDashboard',      label: 'POB Dashboard',       icon: 'target'    },
  { id: 'businessPotential', label: 'Business Potential',  icon: 'trending'  },
  { id: 'brandPerformance',  label: 'Brand Performance',   icon: 'award'     },
  { id: 'orderAnalytics',    label: 'Order Analytics',     icon: 'chart'     },
  { id: 'marketResearch',    label: 'Market Research',     icon: 'compass'   },
  { id: 'marketingDashboard',label: 'Marketing',           icon: 'megaphone' },
  { id: 'productAvailability',label: '4-Month Availability',icon: 'calendar' },
  { id: 'notifications',     label: 'Notifications',       icon: 'bell'      },
  { id: 'reports',           label: 'Reports',             icon: 'file'      },
  { id: 'adminWorkspace',    label: 'Admin Workspace',     icon: 'shield'    },
  { id: 'productMaster',     label: 'Product Master',      icon: 'pill'      },
  // New screens for the 4 rebuilt roles (Pharmacy, Sales Exec, Sales
  // Supervisor, Telecaller) — entirely new module IDs so none of the old
  // ones above are repurposed or at risk; Warehouse/SCM/Marketing/Admin/
  // Country Manager keep using the old ones, untouched.
  { id: 'myOrdersV2',        label: 'Orders',              icon: 'clipboard' },
  { id: 'myInvoicesV2',      label: 'Invoices',            icon: 'file'      },
  { id: 'myPromotionsV2',    label: 'Promotions',          icon: 'megaphone' },
  { id: 'myProfileV2',       label: 'Profile',             icon: 'store'     },
  { id: 'seOrders',          label: 'Orders',              icon: 'clipboard' },
  { id: 'sePharmacies',      label: 'Pharmacies',          icon: 'store'     },
  { id: 'sePob',             label: 'POB Dashboard',       icon: 'target'    },
  { id: 'seBusinessPotential', label: 'Business Potential', icon: 'trending' },
  { id: 'ssOrders',          label: 'Orders',              icon: 'clipboard' },
  { id: 'ssPob',             label: 'POB Dashboard',       icon: 'target'    },
  { id: 'ssBusinessPotential', label: 'Business Potential', icon: 'trending' },
  { id: 'tcOrders',          label: 'Orders',              icon: 'clipboard' },
  { id: 'approvalFlow',      label: 'Approval Flow',       icon: 'users'     },
  // New screens for Warehouse / SCM / Marketing (independent-data rebuild).
  { id: 'warehouseInventory',label: 'Inventory',           icon: 'boxes'     },
  { id: 'warehouseOrders',   label: 'Orders',              icon: 'clipboard' },
  { id: 'scmStock',          label: 'Stock',               icon: 'boxes'     },
  { id: 'scmOOS',            label: 'Out of Stock',        icon: 'alert'     },
  { id: 'campaignManagement',label: 'Campaign Management', icon: 'megaphone' },
]

const MODULE_BY_ID = Object.fromEntries(MODULES.map(m => [m.id, m]))

export const ROLES = [
  {
    id: 'pharmacy', label: 'Pharmacy User',
    desc: 'Not in scope for this build', icon: 'store',
    // Disabled, deliberately NOT removed: the pharmacy-facing app hasn't
    // been confirmed as in scope. The screens and data are all still here
    // and working — flip `disabled` to false to bring the role straight
    // back. The pharmacy's part in the revised flow (OTP confirmation) is
    // still fully exercised via the Order Approval Flow view.
    disabled: true,
    modules: ['myOrdersV2','myInvoicesV2','myPromotionsV2','myProfileV2']
  },
  {
    id: 'salesExec', label: 'Sales Executive',
    desc: 'Book orders, manage pharmacies, track POB', icon: 'briefcase',
    modules: ['seOrders','approvalFlow','sePharmacies','sePob','seBusinessPotential']
  },
  {
    id: 'salesSupervisor', label: 'Sales Supervisor',
    desc: 'Team oversight, POB and business potential', icon: 'users',
    modules: ['ssOrders','approvalFlow','ssPob','ssBusinessPotential']
  },
  {
    id: 'telecaller', label: 'Telecaller',
    desc: 'Validate incoming orders', icon: 'phone',
    modules: ['tcOrders']
  },
  {
    id: 'warehouse', label: 'Warehouse User',
    desc: 'Allocate stock, manage dispatch readiness', icon: 'boxes',
    // Rebuilt under the new independent-data architecture: single-warehouse
    // inventory (no reserved/in-transit split — that's SCM's cross-warehouse
    // concern), and orders with partial/complete stock allocation + dispatch.
    modules: ['warehouseInventory','warehouseOrders']
  },
  {
    id: 'scm', label: 'SCM User',
    desc: 'Supply planning, procurement, inventory visibility', icon: 'link',
    // Rebuilt: cross-warehouse stock (no rebalance banner — that mechanic
    // depended on the scenario toggle, which is gone) and a permanently
    // populated OOS view (previously empty since it depended on switching to
    // the now-removed "Supply Disruption" scenario). Notifications removed
    // per the request. productAvailability (4-Month Trend) intentionally
    // left on the old architecture for now — it still renders correctly
    // (just without a disruption narrative) and wasn't explicitly in scope.
    modules: ['scmStock','scmOOS','productAvailability']
  },
  {
    id: 'marketing', label: 'Marketing User',
    desc: 'Campaigns, promotions & marketing analytics', icon: 'megaphone',
    // Campaign Management rebuilt with independent data (renamed from
    // "Marketing" per the request). Market Research and Notifications
    // removed. Brand Performance deliberately left untouched — old
    // architecture, as explicitly requested ("keep brand performance as is").
    modules: ['campaignManagement','brandPerformance']
  },
  {
    id: 'admin', label: 'Admin',
    desc: 'User management & master data', icon: 'shield',
    // Point 14: narrowed per BRD §4.8 — user management + master data only.
    // NOT YET REBUILT (adminWorkspace/productMaster are the old components,
    // still fine to use as-is since this role's scope was always narrow) —
    // see plan doc for the User Management tab's data-independence pass.
    modules: ['adminWorkspace','productMaster']
  },
  {
    id: 'countryManager', label: 'Cluster Head',
    desc: 'Full business visibility across all dashboards', icon: 'trending',
    // Point 14 scope, updated: notifications removed per request; product
    // master added (Country Manager is "Admin & above" per BRD M2's access
    // note). oosDashboard now maps to the same fixed, permanently-populated
    // OOS view built for SCM (see AppShell's MODULE_MAP) rather than the old
    // scenario-locked one that showed 0 rows.
    modules: ['stockDashboard','oosDashboard','invoiceBilling','pobDashboard','businessPotential',
              'brandPerformance','orderAnalytics','marketResearch','marketingDashboard',
              'productAvailability','reports','productMaster']
  },
]

const ROLE_BY_ID = Object.fromEntries(ROLES.map(r => [r.id, r]))

export const SCENARIOS = [
  { id: 'steady',     label: 'Steady-State Growth'    },
  { id: 'disruption', label: 'Supply Disruption'       },
  { id: 'growth',     label: 'High-Growth Territory'   },
]

const DEFAULT_PHARMACY_ID = 'PH-Z-0002'

const state = reactive({
  role: null,
  scenario: 'steady',
  activeModule: null,
  activePharmacyId: null,
  sessionCampaigns: [],
  sessionOrders: [],       // Phase 5: orders created via the Order Booking form this session
  sessionPharmacies: [],   // Phase 7: pharmacies created via the Pharmacy Master "+ Add Pharmacy" form
  visitNotes: {},          // Phase 7: customerId -> [{ timestamp, note }] field-visit log
  stockOverrides: {},      // Phase 8: `${sku}|${warehouse}` -> { availableQty, lowStock, oosStatus } after a rebalance transfer
  rbacOverrides: {},       // Phase 10: roleId -> array of module ids, set by the Admin Workspace RBAC Configurator
  // Phase 1 — mutable state engine. These hold session-only edits layered on
  // top of the scenario-generated baseline from mockData.js. Keyed by stable
  // identifiers (orderNo / customerId) so they survive scenario switches.
  orderOverrides: {},      // orderNo -> { stageIndex?, rejected?, rejectRemarks?, lrNumber? }
  billingOverrides: {},    // orderNo -> billing stage string
  potentialOverrides: {},  // customerId -> { potentialScore, potentialClassification, dailyFootfall, monthlyFootfall }
  creditOverrides: {},     // customerId -> true once a manager credit override has been granted this session
  // Phase 3 — global search + SLA simulation
  simulateSLABreaches: false,
  pendingFocus: null,      // { type: 'pharmacy'|'order'|'stock', id, query? } set by GlobalSearch, consumed by the destination module
})

function selectRole(roleId) {
  state.role = roleId
  state.activeModule = ROLE_BY_ID[roleId]?.modules[0] || null
  state.activePharmacyId = roleId === 'pharmacy' ? DEFAULT_PHARMACY_ID : null
}
function backToRoleSelect() { state.role = null; state.activeModule = null }
function setScenario(id) { state.scenario = id }
function setActiveModule(id) { state.activeModule = id }
function setActivePharmacy(id) { state.activePharmacyId = id }
function addSessionCampaign(c) { state.sessionCampaigns.push(c) }

// Phase 5: Order Booking actions ---------------------------------------------
function createOrder({ customerId, productSku, quantity, schemeApplied, expectedDeliveryDate }) {
  const product = Data.getProducts().find(p => p.sku === productSku)
  const pharmacy = Data.getPharmacies(state.scenario).find(p => p.customerId === customerId)
  if (!product || !pharmacy || !quantity || quantity <= 0) return null
  const multiplier = Data.getSchemeMultiplier(schemeApplied)
  const orderValue = Math.round(quantity * product.basePrice * multiplier * 100) / 100
  // Continue the sequence after the seeded dataset rather than a hardcoded
  // offset — P0 grew the baseline from 30 to 120 orders, and a fixed "31"
  // would have numbered brand-new orders in the middle of existing history.
  const orderNo = `ORD-2026-${String(Data.ORDERS.length + 1 + state.sessionOrders.length).padStart(5, '0')}`
  const order = {
    orderNo, customerId,
    pharmacyName: pharmacy.pharmacyName,
    orderDate: '2026-07-28',
    productSku, productName: product.productName,
    quantity, schemeApplied: schemeApplied || null,
    orderValue,
    expectedDeliveryDate: expectedDeliveryDate || '2026-08-02',
    currentStage: 'Order Received',
    trafficLight: 'Green',
    tatDays: 0,
  }
  state.sessionOrders.push(order)
  return order
}

function requestCreditOverride(customerId) {
  state.creditOverrides[customerId] = true
}

// Phase 7: Pharmacy Master actions -------------------------------------------
const CATEGORY_CREDIT_LIMIT_P7 = { A: 15000, B: 8000, C: 4000 };

// Duplicate check against Drug License No. or Mobile 1 — checked live as the
// user types, against both baseline and session-created pharmacies.
function findDuplicatePharmacy({ drugLicenseNo, mobile1 }) {
  const all = [...Data.PHARMACIES, ...state.sessionPharmacies]
  return all.find(p =>
    (drugLicenseNo && p.drugLicenseNo.trim().toLowerCase() === drugLicenseNo.trim().toLowerCase()) ||
    (mobile1 && p.mobile1.replace(/\s+/g, '') === mobile1.replace(/\s+/g, ''))
  ) || null
}

// New pharmacies use the blueprint's PH-2026-XXXX format explicitly — distinct
// from the legacy baseline records' PH-Z-XXXX format, since these are new
// onboarding-era IDs, not a renumbering of existing accounts.
function createPharmacy({ pharmacyName, ownerName, mobile1, mobile2, email1, drugLicenseNo, address, city, territory, dailyFootfall, monthlyFootfall }) {
  if (!pharmacyName || !ownerName || !mobile1 || !drugLicenseNo) return null
  const customerId = `PH-2026-${String(1 + state.sessionPharmacies.length).padStart(4, '0')}`
  const df = Number(dailyFootfall) || 40
  const mf = Number(monthlyFootfall) || df * 27
  const score = Math.max(20, Math.min(100, Math.round(df / 3 + mf / 200)))
  const pharmacy = {
    customerId, pharmacyName, ownerName,
    mobile1, mobile2: mobile2 || null, email1: email1 || null, email2: null,
    drugLicenseNo, address: address || '—', city: city || territory, province: territory,
    pincode: '', territory,
    assignedSalesExec: store.currentRole?.value?.label || 'Unassigned',
    dailyFootfall: df, monthlyFootfall: mf,
    category: 'C', creditLimit: CATEGORY_CREDIT_LIMIT_P7.C,
    potentialScore: score, potentialClassification: Data.classifyPotential(score),
    otpVerified: false, growthCohort: false,
  }
  state.sessionPharmacies.push(pharmacy)
  return pharmacy
}

// Dual-OTP verification gate: mock-confirms 4 channels (Mobile 1, Mobile 2,
// Email 1, Email 2 — skipping any the pharmacy doesn't have on file) and
// flips the account from Pending OTP to Activated.
function verifyPharmacyOtp(customerId) {
  const sessionMatch = state.sessionPharmacies.find(p => p.customerId === customerId)
  if (sessionMatch) { sessionMatch.otpVerified = true; return }
  // Baseline pharmacy: reuse the potentialOverrides merge mechanism — it
  // spreads whatever keys are present onto the pharmacy object, so adding
  // otpVerified here works without a new override map or new merge logic.
  state.potentialOverrides[customerId] = { ...(state.potentialOverrides[customerId] || {}), otpVerified: true }
}

function addVisitNote(customerId, note) {
  if (!note || !note.trim()) return
  const entry = { timestamp: new Date().toLocaleString('en-ZW', { dateStyle: 'medium', timeStyle: 'short' }), note: note.trim() }
  state.visitNotes[customerId] = [...(state.visitNotes[customerId] || []), entry]
}

// Phase 8: SCM rebalance transfer — moves quantity from an excess warehouse
// to a deficit one for the same SKU, clearing the deficit warehouse's
// low/OOS flag. Leaves oosDate/replenishmentDate/delayReasonCode untouched
// (those describe the original disruption event's metadata); the boolean
// flags are what drive the dashboard's healthy/low/OOS badge, so clearing
// them is what actually resolves the visible shortage.
function transferStock(sku, fromWarehouse, toWarehouse, qty) {
  const current = mergedStock.value
  const fromRow = current.find(s => s.productSku === sku && s.warehouse === fromWarehouse)
  const toRow = current.find(s => s.productSku === sku && s.warehouse === toWarehouse)
  if (!fromRow || !toRow) return
  const moveQty = Math.min(qty, fromRow.availableQty)
  const newToQty = toRow.availableQty + moveQty
  state.stockOverrides[`${sku}|${fromWarehouse}`] = { ...(state.stockOverrides[`${sku}|${fromWarehouse}`]||{}), availableQty: fromRow.availableQty - moveQty }
  state.stockOverrides[`${sku}|${toWarehouse}`] = { ...(state.stockOverrides[`${sku}|${toWarehouse}`]||{}), availableQty: newToQty, lowStock: newToQty < 140, oosStatus: false }
}

// Phase 10: RBAC Configurator — toggling a module for a role has a REAL
// effect on navigation (visibleModules above), not just a cosmetic checkbox.
// Admin's own access to the Admin Workspace module is protected from being
// toggled off, so a demo presenter can't accidentally lock themselves out of
// the screen that controls access. If the role currently being viewed loses
// access to the module it's actively on, this redirects to that role's first
// remaining visible module rather than leaving the screen stuck on a
// now-inaccessible view.
function toggleRbacModule(roleId, moduleId) {
  if (roleId === 'admin' && moduleId === 'adminWorkspace') return
  const role = ROLE_BY_ID[roleId]
  const current = state.rbacOverrides[roleId] || [...role.modules]
  const has = current.includes(moduleId)
  const next = has ? current.filter(id => id !== moduleId) : [...current, moduleId]
  state.rbacOverrides[roleId] = next
  if (roleId === state.role && state.activeModule === moduleId && !next.includes(moduleId)) {
    state.activeModule = next[0] || null
  }
}
function isModuleEnabledForRole(roleId, moduleId) {
  const role = ROLE_BY_ID[roleId]
  const current = state.rbacOverrides[roleId] || role.modules
  return current.includes(moduleId)
}

// Credit utilization for a pharmacy: outstanding (pre-payment) invoice value
// against its credit limit. Plain function, not a computed, since it takes a
// parameter — components call it from inside their own computed properties,
// which stay reactive because this reads mergedInvoices.value/mergedPharmacies.value.
const PRE_PAYMENT_STAGES = ['Order Confirmed', 'Invoice Pending', 'Invoice Generated', 'Payment Pending', 'Payment Reported']
function getCreditUsage(customerId) {
  const pharmacy = mergedPharmacies.value.find(p => p.customerId === customerId)
  const limit = pharmacy ? pharmacy.creditLimit : 0
  const used = mergedInvoices.value
    .filter(i => i.customerId === customerId && PRE_PAYMENT_STAGES.includes(i.billingStage))
    .reduce((s, i) => s + i.invoiceValue, 0)
  return { used, limit, pct: limit ? Math.round((used / limit) * 100) : 0, overridden: !!state.creditOverrides[customerId] }
}

// --- Phase 1 actions -------------------------------------------------------
// Each action only ever writes to the override maps above; nothing in
// mockData.js is mutated, so scenario baselines stay pure and reproducible.

function advanceOrderStage(orderNo) {
  const current = mergedOrders.value.find(o => o.orderNo === orderNo)
  if (!current || current.rejected) return
  const idx = Data.STAGES.indexOf(current.currentStage)
  if (idx === -1 || idx >= Data.STAGES.length - 1) return
  state.orderOverrides[orderNo] = { ...(state.orderOverrides[orderNo] || {}), stageIndex: idx + 1 }
}

// Phase 9: added a categorized reason alongside the free-text remarks — the
// Cancellation Reason Breakdown chart needs a fixed set of categories to
// aggregate, not free text.
export const REJECTION_REASONS = ['Pharmacy Cancelled', 'Stock Unavailable', 'Credit Hold', 'Duplicate Order', 'Pricing Dispute', 'Other'];
function rejectOrder(orderNo, remarks, reason) {
  state.orderOverrides[orderNo] = { ...(state.orderOverrides[orderNo] || {}), rejected: true, rejectRemarks: remarks || '', rejectReason: reason || 'Other' }
}

function allocateStock(orderNo) {
  // BUG FIX: this previously set stageIndex to 'Stock Allocation' itself —
  // an order already sitting at Stock Allocation would click "Reserve &
  // Allocate Stock" and see... no change, because it was already there.
  // The action represents completing that step, so it must advance to the
  // NEXT stage, Warehouse Processing.
  const idx = Data.STAGES.indexOf('Warehouse Processing')
  state.orderOverrides[orderNo] = { ...(state.orderOverrides[orderNo] || {}), stageIndex: idx }
}

function markDispatched(orderNo, lrNumber) {
  const idx = Data.STAGES.indexOf('Dispatched')
  state.orderOverrides[orderNo] = { ...(state.orderOverrides[orderNo] || {}), stageIndex: idx, lrNumber: lrNumber || '' }
}

function markPaymentReceived(orderNo) {
  // Finance/Admin confirmation is authoritative regardless of whether the
  // pharmacy reported first — a bank-reconciled payment is valid even if the
  // self-report step was skipped. Preserve any existing report metadata
  // rather than overwrite it, so Finance can still see what was reported.
  const existing = state.billingOverrides[orderNo] || {}
  state.billingOverrides[orderNo] = { ...existing, stage: 'Payment Received' }
}

function reportPaymentMade(orderNo, reference, method) {
  state.billingOverrides[orderNo] = {
    ...(state.billingOverrides[orderNo] || {}),
    stage: 'Payment Reported',
    reportedReference: reference || '',
    reportedMethod: method || '',
    reportedDate: '2026-07-29',
  }
}

// Phase 4: Pharmacy OTP confirmation gate. The blueprint's lifecycle is
// Order Received → Pharmacy OTP → Telecaller Validation → ... Rather than
// inserting a 9th named stage (which would shift every STAGES.indexOf()
// comparison the Invoice & Billing module relies on — already verified exact
// in Phase 1/2), OTP confirmation is modeled as a flag on the Order Received
// stage that auto-advances to Telecaller Validation once confirmed. The
// stepper still shows all 8 stages; the OTP step is the gate that unlocks
// the first transition.
function confirmOrderOtp(orderNo) {
  const idx = Data.STAGES.indexOf('Telecaller Validation')
  state.orderOverrides[orderNo] = { ...(state.orderOverrides[orderNo] || {}), otpConfirmed: true, stageIndex: idx }
}

function recalculatePotentialScore(customerId, dailyFootfall, monthlyFootfall) {
  const df = Number(dailyFootfall) || 0
  const mf = Number(monthlyFootfall) || 0
  const score = Math.max(20, Math.min(100, Math.round(df / 3 + mf / 200)))
  state.potentialOverrides[customerId] = {
    potentialScore: score,
    potentialClassification: Data.classifyPotential(score),
    dailyFootfall: df,
    monthlyFootfall: mf,
  }
}

// Resets ALL session mutations back to the scenario baseline. This
// previously cleared only orderOverrides/billingOverrides/potentialOverrides
// — 3 of the 10 mutable keys — silently leaving session-created orders and
// pharmacies, visit notes, stock transfers, RBAC changes and credit
// overrides in place, so "reset" didn't actually reset. Deliberately does
// NOT touch role, scenario, activeModule or activePharmacyId: those are
// navigation state, not demo data, and clearing them would eject the
// presenter to the role picker mid-walkthrough.
function resetSessionEdits() {
  state.orderOverrides = {}
  state.billingOverrides = {}
  state.potentialOverrides = {}
  state.creditOverrides = {}
  state.stockOverrides = {}
  state.rbacOverrides = {}
  state.visitNotes = {}
  state.sessionOrders = []
  state.sessionPharmacies = []
  state.sessionCampaigns = []
  state.simulateSLABreaches = false
  state.pendingFocus = null
}

// --- Phase 3 actions --------------------------------------------------------
function toggleSLASimulation() { state.simulateSLABreaches = !state.simulateSLABreaches }

function setPendingFocus(focus) { state.pendingFocus = focus }
function clearPendingFocus() { state.pendingFocus = null }

// Deterministic string hash so the SLA simulation overlay is stable across
// re-renders (no flicker) without needing to persist per-order state.
function hashStr(s) {
  let h = 0
  for (let i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0 }
  return Math.abs(h)
}

const currentRole     = computed(() => state.role ? ROLE_BY_ID[state.role] : null)
const visibleModules  = computed(() => {
  if (!currentRole.value) return []
  const ids = state.rbacOverrides[state.role] || currentRole.value.modules
  return ids.map(id => MODULE_BY_ID[id]).filter(Boolean)
})
const activeModuleMeta= computed(() => state.activeModule ? MODULE_BY_ID[state.activeModule] : null)
const scenarioSummary = computed(() => Data.getScenarioSummary(state.scenario))
const activePharmacy  = computed(() => state.activePharmacyId
  ? Data.getPharmacies(state.scenario).find(p => p.customerId === state.activePharmacyId)
  : null)
const scenarioRelevantToActivePharmacy = computed(() =>
  state.activePharmacyId ? Data.isScenarioRelevantToPharmacy(state.scenario, state.activePharmacyId) : true)

// --- Phase 1: baseline scenario data merged with session overrides ---------
const mergedOrders = computed(() => [...Data.getOrders(state.scenario), ...state.sessionOrders].map(o => {
  const ov = state.orderOverrides[o.orderNo]
  let stage = o.currentStage
  let rejected = false, rejectRemarks = null, rejectReason = null, lrNumber = o.lrNumber || null, otpConfirmed = false
  if (ov) {
    stage = ov.stageIndex !== undefined ? Data.STAGES[ov.stageIndex] : o.currentStage
    rejected = !!ov.rejected
    rejectRemarks = ov.rejectRemarks || null
    rejectReason = ov.rejectReason || null
    lrNumber = ov.lrNumber || lrNumber
    otpConfirmed = !!ov.otpConfirmed
  }
  // An order already past "Order Received" at baseline generation time must
  // have implicitly cleared the OTP gate, or it couldn't have progressed.
  if (!otpConfirmed && Data.STAGES.indexOf(stage) > Data.STAGES.indexOf('Order Received')) otpConfirmed = true
  let trafficLight = o.trafficLight
  let tatDays = o.tatDays
  // Phase 3: Simulate SLA Breaches — deterministic (hash-based) overlay, not
  // random-per-render, so toggling on/off gives a stable, reproducible demo
  // rather than a different result every time Vue recomputes this.
  if (state.simulateSLABreaches && stage !== 'Delivered' && !rejected) {
    const h = hashStr(o.orderNo) % 3
    if (h === 0) { trafficLight = 'Red'; tatDays = tatDays + 10 }
    else if (h === 1) { trafficLight = 'Orange'; tatDays = tatDays + 5 }
  }
  return { ...o, currentStage: stage, rejected, rejectRemarks, rejectReason, lrNumber, otpConfirmed, trafficLight, tatDays }
}))

const mergedInvoices = computed(() => Data.getInvoices(state.scenario).map(i => {
  const ov = state.billingOverrides[i.orderNo]
  if (!ov) return i
  return {
    ...i,
    billingStage: ov.stage,
    reportedReference: ov.reportedReference || null,
    reportedMethod: ov.reportedMethod || null,
    reportedDate: ov.reportedDate || null,
  }
}))

const mergedPharmacies = computed(() => [...Data.getPharmacies(state.scenario), ...state.sessionPharmacies].map(p => {
  const ov = state.potentialOverrides[p.customerId]
  return ov ? { ...p, ...ov } : p
}))

// Phase 8: stock merged with any SCM rebalance transfers applied this session.
const mergedStock = computed(() => Data.getStock(state.scenario).map(s => {
  const ov = state.stockOverrides[`${s.productSku}|${s.warehouse}`]
  return ov ? { ...s, ...ov } : s
}))

const data = {
  pharmacies:     mergedPharmacies,
  products:       computed(() => Data.getProducts()),
  orders:         mergedOrders,
  invoices:       mergedInvoices,
  stock:          mergedStock,
  pob:            computed(() => Data.getPOB(state.scenario)),
  brandPerformance: computed(() => Data.getBrandPerformance(state.scenario)),
  campaigns:      computed(() => Data.getCampaigns(state.scenario)),
  notifications:  computed(() => Data.getNotifications(state.scenario)),
  myOrders:       computed(() => state.activePharmacyId ? mergedOrders.value.filter(o => o.customerId === state.activePharmacyId) : []),
  myInvoices:     computed(() => state.activePharmacyId ? mergedInvoices.value.filter(i => i.customerId === state.activePharmacyId) : []),
  myCampaigns:    computed(() => state.activePharmacyId ? Data.getCampaignsForPharmacy(state.scenario, state.activePharmacyId) : []),
}

// =============================================================================
// NEW ARCHITECTURE — Pharmacy / Sales Executive / Sales Supervisor / Telecaller
// =============================================================================
// Per the overhaul: these 4 roles no longer read from the shared scenario-
// diffed mockData.js pool above. Each has its own independent, hand-authored
// dataset (src/data/{pharmacy,salesExec,salesSupervisor,telecaller}/) so no
// screen's demo depends on another role's state, and no scenario toggle is
// needed — the variety is baked into the data permanently.
//
// Everything ABOVE this line (mergedOrders, mergedInvoices, scenarioOverrides,
// etc.) is left untouched and still serves Warehouse / SCM / Marketing /
// Admin / Country Manager, which have not been rebuilt yet.
import * as PharmacyData from '@/data/pharmacy/orders.js'
import * as SalesExecData from '@/data/salesExec/orders.js'
import * as SalesSupervisorData from '@/data/salesSupervisor/orders.js'
import * as TelecallerData from '@/data/telecaller/orders.js'
import * as AllOrdersData from '@/data/shared/allOrders.js'
import * as WarehouseData from '@/data/warehouse/inventory.js'
import * as WarehouseOrdersData from '@/data/warehouse/orders.js'
import * as ScmData from '@/data/scm/stock.js'
import * as MarketingData from '@/data/marketing/campaigns.js'
import { STAGES as V2_STAGES, STAGE, LATERAL_STATES, FULFILMENT } from '@/data/shared/orderModel.js'
import { PRODUCTS as V2_PRODUCTS, getProduct as v2GetProduct, computeLineValue } from '@/data/shared/products.js'
import { classifyPotential, computePotentialScore, CATEGORY_CEILING } from '@/data/shared/potential.js'
import { buildPOBMetrics, computeGrowthPct, productMix } from '@/data/shared/analytics.js'

// Deep-cloned once at module load so each role can freely mutate its own
// session copy without ever touching the source data module (which stays
// pure/reproducible, same discipline as the old architecture's overrides).
const pharmacyOrders = reactive(JSON.parse(JSON.stringify(PharmacyData.MY_ORDERS)))
// ONE shared, reactive order pool. Every role's queue below is a computed
// FILTER over this same array (by stage / exec / etc), not a separate copy —
// so an order the MR raises is the same live object Supervisor approves,
// Telecaller validates, and Warehouse allocates/dispatches.
const sharedOrders = reactive(JSON.parse(JSON.stringify(AllOrdersData.ALL_ORDERS)))
const salesExecPharmacies = reactive(JSON.parse(JSON.stringify(SalesExecData.MY_PHARMACIES)))
salesExecPharmacies.forEach(p => { if (!p.contacts) p.contacts = [] })
const telecallerScmRequests = reactive(JSON.parse(JSON.stringify(TelecallerData.SCM_REQUESTS)))
// The MR's own scoped view — everything they've personally raised. Other
// execs' orders (seeded for team-oversight realism) stay visible to the
// Supervisor but aren't surfaced as "mine" here.
const myExecOrders = computed(() => sharedOrders.filter(o => o.execName === 'Tendai Moyo'))
const marketingCampaigns = reactive(JSON.parse(JSON.stringify(MarketingData.CAMPAIGNS)))

function marketingCreateCampaign({ campaignName, productSku, startDate, endDate, territories }) {
  if (!campaignName || !productSku || !territories.length) return null
  const product = v2GetProduct(productSku)
  const campaign = {
    campaignName, productSku, productName: product?.productName || productSku,
    startDate, endDate, reach: 0, effectivenessScore: 0, adoptionRate: 0,
    territoryPerformance: territories.map(t => ({ territory: t, performanceScore: 0 })),
  }
  marketingCampaigns.push(campaign)
  return campaign
}

function v2AddHistory(order, stage) {
  order.statusHistory.push({ stage, timestamp: new Date().toISOString() })
  order.currentStage = stage
}
function v2AddComment(order, from, text) {
  order.comments = order.comments || []
  order.comments.push({ from, text, timestamp: new Date().toISOString() })
}

// --- Pharmacy actions --------------------------------------------------
function pharmacyConfirmOrder(orderNo) {
  const o = pharmacyOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== 'Order Received') return
  v2AddHistory(o, 'Telecaller Validation')
  o.invoice = o.invoice || { invoiceNo: null, invoiceDate: null, baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Order Confirmed' }
}
function pharmacyCancelOrder(orderNo, reason) {
  const o = pharmacyOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== 'Order Received') return
  o.currentStage = LATERAL_STATES.CANCELLED
  o.cancelledBy = 'pharmacy'
  o.cancelReason = reason || ''
}
// Point 11: two payment paths, both settle the invoice — "pay through the
// platform" is a mock payment that goes straight to Payment Received;
// "record a payment as paid" is a note that it was settled outside the
// system, no confirmation step, since no Finance role exists to confirm it.
function pharmacyPayViaPlatform(orderNo) {
  const o = pharmacyOrders.find(x => x.orderNo === orderNo)
  if (!o || !o.invoice) return
  o.invoice.billingStage = 'Payment Received'
  o.invoice.paymentMethod = 'Platform Payment'
  o.invoice.paymentDate = new Date().toISOString().slice(0, 10)
}
function pharmacyRecordPaymentAsPaid(orderNo, note) {
  const o = pharmacyOrders.find(x => x.orderNo === orderNo)
  if (!o || !o.invoice) return
  o.invoice.billingStage = 'Payment Received'
  o.invoice.paymentMethod = 'Recorded (settled outside the platform)'
  o.invoice.paymentNote = note || ''
  o.invoice.paymentDate = new Date().toISOString().slice(0, 10)
}

// --- Sales Executive / MR actions ----------------------------------------
let seOrderSeq = sharedOrders.length

// Point 1: an MR raising an order on a pharmacy's behalf can't self-confirm
// it — the customer has to. Creating the order generates an OTP "sent" to
// the pharmacy's registered mobile and parks the order at the OTP gate.
// There's no SMS gateway in the prototype, so the code is surfaced in the UI
// rather than pretending it was delivered out-of-band.
function generateOtp() {
  return String(Math.floor(1000 + Math.random() * 9000))
}

function salesExecCreateOrder({ customerId, lineItems }) {
  const pharmacy = salesExecPharmacies.find(p => p.customerId === customerId)
  if (!pharmacy || !lineItems || !lineItems.length) return null
  seOrderSeq += 1
  const built = lineItems.map(li => {
    const { value, schemeApplied, freeQuantity, totalQuantity } = computeLineValue(li.productSku, li.quantity)
    const p = v2GetProduct(li.productSku)
    return {
      productSku: li.productSku, productName: p.productName, quantity: li.quantity, unitPrice: p.unitPrice,
      schemeApplied, freeQuantity, totalQuantity, lineValue: value,
      allocated: false, fulfilmentStatus: FULFILMENT.PENDING, fulfilledQty: null,
      warehouseFulfilmentStatus: FULFILMENT.PENDING, warehouseAllocatedQty: null,
    }
  })
  const now = new Date().toISOString()
  const order = {
    orderNo: `ORD-SE-${String(seOrderSeq).padStart(3, '0')}`,
    customerId, pharmacyName: pharmacy.pharmacyName,
    execName: 'Tendai Moyo', territory: pharmacy.territory || null, category: pharmacy.category || null,
    orderDate: now.slice(0, 10),
    lineItems: built,
    orderValue: Math.round(built.reduce((s, li) => s + li.lineValue, 0) * 100) / 100,
    currentStage: STAGE.OTP_CONFIRMATION,
    otpCode: generateOtp(),
    otpSentTo: pharmacy.mobile1,
    otpVerified: false,
    approvedBy: null, trafficLight: 'Green', lrNumber: null, cancelledBy: null, cancelReason: null,
    statusHistory: [
      { stage: STAGE.ORDER_RECEIVED, timestamp: now },
      { stage: STAGE.OTP_CONFIRMATION, timestamp: now },
    ],
    comments: [],
  }
  sharedOrders.push(order)
  return order
}

// The pharmacy's confirmation. Correct code -> releases to the supervisor.
function confirmOrderOtpCode(orderNo, code) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== STAGE.OTP_CONFIRMATION) return { ok: false, reason: 'not-pending' }
  if (o.otpCode && String(code).trim() !== String(o.otpCode)) return { ok: false, reason: 'wrong-code' }
  o.otpVerified = true
  v2AddHistory(o, STAGE.SUPERVISOR_APPROVAL)
  return { ok: true }
}
function declineOrderOtp(orderNo, reason) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== STAGE.OTP_CONFIRMATION) return
  o.currentStage = LATERAL_STATES.CANCELLED
  o.cancelledBy = 'pharmacy'
  o.cancelReason = reason || 'Declined at OTP confirmation'
}
function resendOtp(orderNo) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== STAGE.OTP_CONFIRMATION) return null
  o.otpCode = generateOtp()
  return o.otpCode
}

let sePharmacySeq = salesExecPharmacies.length
function salesExecCreatePharmacy({ pharmacyName, ownerName, mobile1, city, territory, drugLicenseNo, dailyFootfall, monthlyFootfall, category }) {
  if (!pharmacyName || !ownerName || !mobile1 || !drugLicenseNo) return null
  sePharmacySeq += 1
  const pharmacy = {
    customerId: `PH-SE-${String(sePharmacySeq).padStart(3, '0')}`,
    pharmacyName, ownerName, mobile1, city, territory: territory || city, drugLicenseNo,
    dailyFootfall: Number(dailyFootfall) || 40, monthlyFootfall: Number(monthlyFootfall) || (Number(dailyFootfall) || 40) * 27,
    category: category || 'C',
    contacts: [],
  }
  salesExecPharmacies.push(pharmacy)
  return pharmacy
}
// Point: a contact person at the pharmacy (pharmacist-in-charge, procurement
// contact, etc.) — name/designation/number/email/notes, per the request.
function salesExecAddContact(customerId, { name, designation, mobile, email, notes }) {
  const pharmacy = salesExecPharmacies.find(p => p.customerId === customerId)
  if (!pharmacy || !name) return null
  const contact = { name, designation: designation || '', mobile: mobile || '', email: email || '', notes: notes || '' }
  pharmacy.contacts = pharmacy.contacts || []
  pharmacy.contacts.push(contact)
  return contact
}
// A bounced-back order re-enters the SAME record. It restarts at the
// supervisor gate, not at OTP — the customer already confirmed what they
// wanted; it's the internal approval that needs redoing.
function salesExecResubmit(orderNo) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== LATERAL_STATES.RETURNED) return
  v2AddComment(o, 'Sales Executive', 'Resubmitted after modification.')
  v2AddHistory(o, STAGE.SUPERVISOR_APPROVAL)
}

// --- Sales Supervisor actions --------------------------------------------
// Point 3/4: the supervisor is a real gate. Approving releases the order to
// the telecaller — and ONLY the supervisor can do that, so a telecaller can
// never authenticate an order that hasn't been approved.
function supervisorApproveOrder(orderNo) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== STAGE.SUPERVISOR_APPROVAL) return
  v2AddHistory(o, STAGE.TELECALLER_VALIDATION)
  o.approvedBy = 'Sales Supervisor'
}
function supervisorCancelOrder(orderNo, reason) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o) return
  o.currentStage = LATERAL_STATES.CANCELLED
  o.cancelledBy = 'supervisor'
  o.cancelReason = reason || ''
}
function supervisorAddComment(orderNo, text) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || !text) return
  v2AddComment(o, 'Sales Supervisor', text)
  o.currentStage = LATERAL_STATES.RETURNED
}

// --- Telecaller actions -------------------------------------------------
// Point 4: this is only reachable for orders the supervisor already
// released — the queue below filters to Telecaller Validation, which an
// order can only reach via supervisorApproveOrder.
function telecallerConfirmOrder(orderNo) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== STAGE.TELECALLER_VALIDATION) return
  v2AddHistory(o, STAGE.STOCK_ALLOCATION)
}
function telecallerCancelOrder(orderNo, reason) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o) return
  o.currentStage = LATERAL_STATES.CANCELLED
  o.cancelledBy = 'telecaller'
  o.cancelReason = reason || ''
}
function telecallerAddComment(orderNo, text) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || !text) return
  v2AddComment(o, 'Telecaller', text)
  o.currentStage = LATERAL_STATES.RETURNED
}

// Point 5: mark fulfilment per product line against live inventory. A
// fulfilled qty below the ordered qty is a partial; zero is unavailable.
// Point 5 (corrected): a partial fulfilment must recompute the 10+1 scheme
// against what's ACTUALLY being fulfilled, not the original order quantity —
// you can't earn free units, or be billed, for stock that was never shipped.
// Reuses computeLineValue exactly as the booking form does, just called
// with the fulfilled quantity instead of the ordered one. li.quantity (what
// was ORDERED) stays untouched as the permanent reference; freeQuantity/
// totalQuantity/lineValue become "as currently fulfilled."
function recalcLineForQty(li, qty) {
  const { value, schemeApplied, freeQuantity, totalQuantity } = computeLineValue(li.productSku, qty)
  li.freeQuantity = freeQuantity
  li.totalQuantity = totalQuantity
  li.lineValue = value
  li.schemeApplied = schemeApplied
}
function recalcOrderValue(o) {
  o.orderValue = Math.round(o.lineItems.reduce((s, li) => s + li.lineValue, 0) * 100) / 100
}

function telecallerSetFulfilment(orderNo, productSku, fulfilledQty) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o) return
  const li = o.lineItems.find(x => x.productSku === productSku)
  if (!li) return
  const qty = Math.max(0, Math.min(Number(fulfilledQty) || 0, li.quantity))
  li.fulfilledQty = qty
  li.fulfilmentStatus = qty === 0 ? FULFILMENT.UNAVAILABLE
    : qty < li.quantity ? FULFILMENT.PARTIAL
    : FULFILMENT.FULL
  recalcLineForQty(li, qty)
  recalcOrderValue(o)
}

// Point 6: raise an SCM request, typically off the back of a short line.
let scmReqSeq = telecallerScmRequests.length
function telecallerRaiseScmRequest({ productSku, orderNo, requestedQty, note }) {
  if (!productSku || !requestedQty) return null
  const product = v2GetProduct(productSku)
  scmReqSeq += 1
  const req = {
    requestId: `SCM-REQ-${String(scmReqSeq).padStart(3, '0')}`,
    productSku, productName: product?.productName || productSku,
    orderNo: orderNo || null,
    requestedQty: Number(requestedQty),
    raisedOn: new Date().toISOString().slice(0, 10),
    status: 'Open',
    note: note || '',
  }
  telecallerScmRequests.push(req)
  return req
}

// --- Warehouse actions ----------------------------------------------------
// Allocate Stock can complete the whole order or only some products — each
// line item carries its own `allocated` flag. The order only advances past
// Stock Allocation once every line is allocated; partial allocation leaves
// it visibly in-progress.
// Warehouse allocation is independent of Telecaller's fulfilment number —
// Warehouse can move it up or down — but Telecaller's figure stays visible
// on the line (li.fulfilledQty untouched) for comparison. Warehouse's own
// decision lives in a separate field so neither overwrites the other.
function warehouseAllocateStock(orderNo, qtyBySku) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== STAGE.STOCK_ALLOCATION) return
  Object.entries(qtyBySku).forEach(([sku, qty]) => {
    const li = o.lineItems.find(x => x.productSku === sku)
    if (!li) return
    const q = Math.max(0, Math.min(Number(qty) || 0, li.quantity))
    li.warehouseAllocatedQty = q
    li.warehouseFulfilmentStatus = q === 0 ? FULFILMENT.UNAVAILABLE
      : q < li.quantity ? FULFILMENT.PARTIAL
      : FULFILMENT.FULL
    li.allocated = true
    // Warehouse is the final say on scheme/value once they've allocated —
    // recalculated the same way, just from Warehouse's own number.
    recalcLineForQty(li, q)
  })
  recalcOrderValue(o)
  if (o.lineItems.every(li => li.allocated)) {
    v2AddHistory(o, STAGE.WAREHOUSE_PROCESSING)
  }
}
function warehouseDispatchOrder(orderNo, lrNumber) {
  const o = sharedOrders.find(x => x.orderNo === orderNo)
  if (!o || o.currentStage !== 'Dispatch Pending') return
  o.lrNumber = lrNumber || o.lrNumber
  v2AddHistory(o, 'Dispatched')
}

const pharmacyV2 = {
  profile: PharmacyData.MY_PHARMACY,
  orders: computed(() => pharmacyOrders),
  promotions: PharmacyData.MY_PROMOTIONS,
  notifications: PharmacyData.MY_NOTIFICATIONS,
  confirmOrder: pharmacyConfirmOrder,
  cancelOrder: pharmacyCancelOrder,
  payViaPlatform: pharmacyPayViaPlatform,
  recordPaymentAsPaid: pharmacyRecordPaymentAsPaid,
}
const salesExecV2 = {
  orders: myExecOrders,
  pharmacies: computed(() => salesExecPharmacies),
  products: V2_PRODUCTS,
  // POB (M9): territory is joined in from the pharmacy roster since orders
  // only carry customerId — everything else derives straight from orders.
  pob: computed(() => {
    const territoryByCustomer = Object.fromEntries(salesExecPharmacies.map(p => [p.customerId, p.territory]));
    const enriched = myExecOrders.value.map(o => ({ ...o, territory: territoryByCustomer[o.customerId] }));
    return buildPOBMetrics(enriched, SalesExecData.POB_MONTHLY_TARGET);
  }),
  // Business Potential (M8): full metrics, outputs and segmentation, all
  // derived live — creating a new order actually moves these numbers.
  businessPotential: computed(() => {
    const rows = salesExecPharmacies.map(p => {
      const orders = myExecOrders.value.filter(o => o.customerId === p.customerId);
      const active = orders.filter(o => o.currentStage !== LATERAL_STATES.CANCELLED);
      const actual = Math.round(active.reduce((s, o) => s + o.orderValue, 0));
      const potentialScore = computePotentialScore(p.dailyFootfall, p.monthlyFootfall);
      const potentialClassification = classifyPotential(potentialScore);
      const potentialValue = CATEGORY_CEILING[p.category] || 2000;
      const avgOrderValue = active.length ? Math.round(actual / active.length) : 0;
      const growthPct = computeGrowthPct(orders);
      const mix = productMix(active);
      return {
        ...p, actual, potentialValue, potentialScore, potentialClassification,
        opportunityGap: Math.max(0, potentialValue - actual),
        orderCount: active.length, avgOrderValue, growthPct,
        topProduct: mix.topProduct, topProductPct: mix.topProductPct, divisionCount: mix.divisionCount,
        monthlyValue: actual, quarterlyValue: actual * 3, annualValue: actual * 12,
      };
    });
    const segmentation = { Platinum: 0, Gold: 0, Silver: 0, Bronze: 0 };
    rows.forEach(r => { segmentation[r.potentialClassification] += 1; });
    return { rows, segmentation };
  }),
  createOrder: salesExecCreateOrder,
  createPharmacy: salesExecCreatePharmacy,
  addContact: salesExecAddContact,
  resubmit: salesExecResubmit,
  confirmOtp: confirmOrderOtpCode,
  declineOtp: declineOrderOtp,
  resendOtp,
}
const salesSupervisorV2 = {
  orders: computed(() => sharedOrders),
  pob: computed(() => {
    const team = buildPOBMetrics(sharedOrders, SalesSupervisorData.TEAM_TARGET)
    const execNames = [...new Set(sharedOrders.filter(o => o.execName).map(o => o.execName))]
    const byIndividual = execNames.map(execName => {
      const execOrders = sharedOrders.filter(o => o.execName === execName)
      const target = SalesSupervisorData.INDIVIDUAL_TARGETS[execName] || 1000
      const active = execOrders.filter(o => o.currentStage !== LATERAL_STATES.CANCELLED)
      const achieved = Math.round(active.reduce((s, o) => s + o.orderValue, 0))
      return { execName, target, achieved, achievementPct: target ? Math.round((achieved / target) * 100) : 0 }
    })
    return { ...team, byIndividual }
  }),
  // Business Potential (M8) at team level: each order already carries
  // territory + pharmacy category, so pharmacies are derived by grouping
  // orders rather than needing a separate roster (this dataset doesn't keep
  // one, unlike Sales Exec's). Footfall isn't tracked per order here, so
  // potential score uses a category-based proxy footfall — a reasonable
  // stand-in given the BRD's own potential-score inputs include footfall
  // data this dataset doesn't carry at the team-oversight level.
  businessPotential: computed(() => {
    const byPharmacy = {};
    sharedOrders.forEach(o => {
      byPharmacy[o.customerId] = byPharmacy[o.customerId] || { customerId: o.customerId, pharmacyName: o.pharmacyName, territory: o.territory, category: o.category, orders: [] };
      byPharmacy[o.customerId].orders.push(o);
    });
    const PROXY_FOOTFALL = { A: { daily: 90, monthly: 2430 }, B: { daily: 60, monthly: 1620 }, C: { daily: 35, monthly: 945 } };
    const rows = Object.values(byPharmacy).map(p => {
      const active = p.orders.filter(o => o.currentStage !== LATERAL_STATES.CANCELLED);
      const actual = Math.round(active.reduce((s, o) => s + o.orderValue, 0));
      const proxy = PROXY_FOOTFALL[p.category] || PROXY_FOOTFALL.C;
      const potentialScore = computePotentialScore(proxy.daily, proxy.monthly);
      const potentialClassification = classifyPotential(potentialScore);
      const potentialValue = CATEGORY_CEILING[p.category] || 2000;
      const avgOrderValue = active.length ? Math.round(actual / active.length) : 0;
      const growthPct = computeGrowthPct(p.orders);
      const mix = productMix(active);
      return {
        customerId: p.customerId, pharmacyName: p.pharmacyName, territory: p.territory, category: p.category,
        actual, potentialValue, potentialScore, potentialClassification,
        opportunityGap: Math.max(0, potentialValue - actual), orderCount: active.length, avgOrderValue, growthPct,
        topProduct: mix.topProduct, topProductPct: mix.topProductPct, divisionCount: mix.divisionCount,
        monthlyValue: actual, quarterlyValue: actual * 3, annualValue: actual * 12,
      };
    });
    const segmentation = { Platinum: 0, Gold: 0, Silver: 0, Bronze: 0 };
    rows.forEach(r => { segmentation[r.potentialClassification] += 1; });
    const byExec = {};
    sharedOrders.forEach(o => {
      if (o.execName) byExec[o.execName] = byExec[o.execName] || 0;
    });
    const gapByExec = Object.keys(byExec).map(execName => {
      const execOrders = sharedOrders.filter(o => o.execName === execName);
      const gap = rows.filter(r => execOrders.some(o => o.customerId === r.customerId)).reduce((s, r) => s + r.opportunityGap, 0);
      return { execName, opportunityGap: gap };
    });
    return { rows, segmentation, totalOpportunityGap: rows.reduce((s, r) => s + r.opportunityGap, 0), byExec: gapByExec };
  }),
  approveOrder: supervisorApproveOrder,
  cancelOrder: supervisorCancelOrder,
  addComment: supervisorAddComment,
}
const telecallerV2 = {
  orders: computed(() => sharedOrders.filter(o => o.currentStage === STAGE.TELECALLER_VALIDATION)),
  // Orders that have moved on from Telecaller Validation — so confirming an
  // order doesn't make it disappear with no trace. Includes the order's
  // fulfilment record (full/partial/unavailable per line) as it stood when
  // sent to Warehouse.
  processedOrders: computed(() => {
    const stageIdx = V2_STAGES.indexOf(STAGE.TELECALLER_VALIDATION)
    return sharedOrders.filter(o => V2_STAGES.indexOf(o.currentStage) > stageIdx || (o.currentStage === LATERAL_STATES.CANCELLED && o.approvedBy))
  }),
  inventory: TelecallerData.LIVE_INVENTORY,
  scmRequests: computed(() => telecallerScmRequests),
  confirmOrder: telecallerConfirmOrder,
  cancelOrder: telecallerCancelOrder,
  addComment: telecallerAddComment,
  setFulfilment: telecallerSetFulfilment,
  raiseScmRequest: telecallerRaiseScmRequest,
}
const warehouseV2 = {
  warehouseName: WarehouseData.MY_WAREHOUSE,
  inventory: WarehouseData.MY_INVENTORY,
  orders: computed(() => {
    const relevant = [STAGE.STOCK_ALLOCATION, STAGE.WAREHOUSE_PROCESSING, STAGE.DISPATCH_PENDING, STAGE.DISPATCHED, STAGE.INVOICE_GENERATED, STAGE.DELIVERED]
    return sharedOrders.filter(o => relevant.includes(o.currentStage) || (o.currentStage === LATERAL_STATES.CANCELLED && o.cancelledBy === 'warehouse'))
  }),
  allocateStock: warehouseAllocateStock,
  dispatchOrder: warehouseDispatchOrder,
}
const scmV2 = {
  warehouses: ScmData.WAREHOUSES,
  stock: ScmData.SCM_STOCK,
  oos: ScmData.SCM_OOS,
}
const marketingV2 = {
  campaigns: computed(() => marketingCampaigns),
  products: V2_PRODUCTS,
  territories: MarketingData.TERRITORIES,
  createCampaign: marketingCreateCampaign,
}

export const store = {
  state, ROLES, MODULES, SCENARIOS,
  selectRole, backToRoleSelect, setScenario, setActiveModule,
  setActivePharmacy, addSessionCampaign,
  advanceOrderStage, rejectOrder, allocateStock, markDispatched,
  markPaymentReceived, reportPaymentMade, recalculatePotentialScore, resetSessionEdits,
  toggleSLASimulation, setPendingFocus, clearPendingFocus,
  confirmOrderOtp, hashStr,
  createOrder, requestCreditOverride, getCreditUsage,
  createPharmacy, verifyPharmacyOtp, addVisitNote, findDuplicatePharmacy,
  transferStock,
  toggleRbacModule, isModuleEnabledForRole,
  currentRole, visibleModules, activeModuleMeta,
  scenarioSummary, activePharmacy, scenarioRelevantToActivePharmacy,
  data,
  pharmacyV2, salesExecV2, salesSupervisorV2, telecallerV2, warehouseV2, scmV2, marketingV2,
}